import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Table, Empty, Input } from "antd";
import AppShell from "../../components/layout/AppShell";
import Badge from "../../components/ui/Badge";
import VaultLedgerTable from "../../components/ui/VaultLedgerTable";
import { getVaultBalance, getVaultLedger } from "../../services/api";
import { getVaultLedgerAnalytics } from "../../services/analytics.api";
import useAnalytics from "../../hooks/useAnalytics";
import AnalyticsSummaryCards from "../../components/ui/AnalyticsSummaryCards";
import DateFilter from "../../components/ui/DateFilter";
import { useSelector } from "react-redux";
import { formatAmount, getMaxDecimalsForCurrency, getDateFilterRange } from "../../utils/index";
import { DEFAULT_PAGE_SIZE_LIMIT, DEFAULT_PAGE_SIZE_OPTIONS, DATE_FILTER_OPTIONS } from "../../utils/constants";

// Kill-switch for this page's analytics <DateFilter />.
const DISABLE_DATE_FILTER_FOR_ANALYTICS = false;

// Kill-switch for this page's ledger-table <DateFilter />.
const DISABLE_DATE_FILTER_FOR_LEDGER = false;

// Kill-switch for the entire Vault Analytics section.
const DISABLE_VAULT_ANALYTICS = true;

// Kill-switch for the entire Transactions table section.
const DISABLE_TRANSACTIONS_TABLE = true;

// Kill-switch for the per-vendor Vault Ledger tables sourced from getVaultBalance().
const DISABLE_VAULT_LEDGER_TABLE = false;

const fmtDate = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

// USDC: 7 decimal places
const fmtUsdc = (n) =>
  Number(n ?? 0).toLocaleString("en-US", { minimumFractionDigits: 6, maximumFractionDigits: 6 });

// Fiat: floor to 2 decimal places (half-round-down)
const fmtFiat = (n) => {
  const floored = Math.floor(Number(n ?? 0) * 100) / 100;
  return floored.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const fmtByCurrency = (n, currency) =>
  (currency || "").toUpperCase() === "USDC" || (currency || "").toUpperCase() === "USDT" ? fmtUsdc(n) : fmtFiat(n);

const statusLabel = (s) => {
  if (!s) return "—";
  const n = s.toLowerCase();
  if (n === "completed" || n === "success" || n === "credit") return "Completed";
  if (n === "debit" || n === "withdrawn") return "Withdrawn";
  if (n === "pending" || n === "processing" || n === "created") return "Processing";
  if (n === "failed" || n === "rejected") return "Failed";
  return s;
};

const statusColor = (s) => {
  const l = statusLabel(s);
  if (l === "Completed") return "text-green-600";
  if (l === "Withdrawn") return "text-purple-500";
  if (l === "Failed") return "text-red-500";
  return "text-orange-500";
};

function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

const Treasury = () => {
  const navigate = useNavigate();
  const userId = useSelector((s) => s.auth.userId);
  const quotePermissions = useSelector((s) => s.auth.quotePermissions);

  useEffect(() => {
    if (quotePermissions.canViewTreasury == false) {
      navigate("/sender/invoices", { replace: true });
    }
  }, [quotePermissions, navigate]);

  // Balance — one entry per vault vendor: { balance: { vendor, balance, currency }, ledger }
  const [vaultEntries, setVaultEntries] = useState([]);
  const [balanceLoading, setBalanceLoading] = useState(true);

  // Transactions (backend-paginated + filtered)
  const [txns, setTxns] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(!DISABLE_TRANSACTIONS_TABLE);

  // Filters & pagination
  const [ledgerDateRange, setLedgerDateRange] = useState(() =>
    getDateFilterRange(DATE_FILTER_OPTIONS.MONTH_TILL_DATE)
  );
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE_LIMIT);
  const debouncedSearch = useDebounce(search, 400);

  const [analyticsDateRange, setAnalyticsDateRange] = useState(() =>
    getDateFilterRange(DATE_FILTER_OPTIONS.MONTH_TILL_DATE)
  );
  const { value: vaultAnalytics, loading: vaultAnalyticsLoading } = useAnalytics(
    getVaultLedgerAnalytics,
    DISABLE_DATE_FILTER_FOR_ANALYTICS
      ? {}
      : { from: analyticsDateRange.startDate, to: analyticsDateRange.endDate },
    { enabled: !DISABLE_VAULT_ANALYTICS }
  );

  const analyticsItems = (vaultAnalytics != null) ? vaultAnalytics.map(({ amount, count, currency, inAmount, net, outAmount }) => {
    if (!count) return null;
    return {
      title: `${currency} Ledger`,
      value: (
        <div className="flex flex-col gap-0.5">
          <span>Total : {formatAmount(amount, getMaxDecimalsForCurrency(currency))}</span>
          <span className="text-green-600">In : {formatAmount(inAmount, getMaxDecimalsForCurrency(currency))}</span>
          <span className="text-red-600">Out : {formatAmount(outAmount, getMaxDecimalsForCurrency(currency))}</span>
          <span className="text-yellow-600">Net : {formatAmount(net, getMaxDecimalsForCurrency(currency))}</span>
        </div>
      ),
      subLabel: `${count ?? 0} Transactions`,
    };
  }).filter(Boolean) : [];

  const walletBalanceItems = vaultEntries.map((entry) => {
    const currency = (entry.balance?.currency || "USDC").toUpperCase();
    return {
      title: `Wallet Balance`,
      value: (fmtByCurrency(entry.balance?.balance, currency) + `  ${currency}`),
    };
  });

  // Placeholder rows so AnalyticsSummaryCards has something to skeleton — the
  // actual vendor count isn't known until getVaultBalance() resolves.
  const WALLET_BALANCE_SKELETON_ITEMS = [{ title: "loading-1" }];

  const walletCurrencies = vaultEntries
    .map((entry) => (entry.balance?.currency || "").toUpperCase())
    .filter(Boolean);
  const walletSubtitle = walletCurrencies.length > 0
    ? `Your ${walletCurrencies.join(" & ")} wallet${walletCurrencies.length > 1 ? "s" : ""}`
    : "Your USDC wallet";

  // ── Load balance once ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!userId) return;
    setBalanceLoading(true);
    getVaultBalance(userId)
      .then((v) => setVaultEntries(Array.isArray(v.data) ? v.data : []))
      .catch(() => setVaultEntries([]))
      .finally(() => setBalanceLoading(false));
  }, [userId]);

  // Reset to page 1 whenever the search query settles
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  // ── Load ledger entries — filtered by date range/search + paginated on the backend ────
  useEffect(() => {
    if (!userId || DISABLE_TRANSACTIONS_TABLE) return;
    setLoading(true);
    getVaultLedger(userId, {
      page,
      limit: pageSize,
      ...(!DISABLE_DATE_FILTER_FOR_LEDGER && ledgerDateRange.startDate ? { from: ledgerDateRange.startDate } : {}),
      ...(!DISABLE_DATE_FILTER_FOR_LEDGER && ledgerDateRange.endDate ? { to: ledgerDateRange.endDate } : {}),
      ...(debouncedSearch ? { searchQuery: debouncedSearch } : {}),
    })
      .then((l) => {
        setTxns(l.data || []);
        setTotal(l.meta?.total || 0);
      })
      .catch(() => {
        setTxns([]);
        setTotal(0);
      })
      .finally(() => setLoading(false));
  }, [userId, ledgerDateRange, page, pageSize, debouncedSearch]);

  // Reset to page 1 whenever the ledger date filter changes
  const changeLedgerDateFilter = (_filter, range) => {
    setLedgerDateRange((prev) =>
      prev.startDate === range.startDate && prev.endDate === range.endDate ? prev : range
    );
    setPage(1);
  };

  const amountDisplay = (entry) => {
    const currency = entry.currency || "USDC";
    const formatted = fmtByCurrency(entry.amount, currency);
    const type = (entry.entryType || entry.type || "").toLowerCase();
    if (type === "credit") return { text: `+${formatted}`, color: "text-green-600" };
    if (type === "debit") return { text: `-${formatted}`, color: "text-red-500" };
    return { text: formatted, color: "text-gray-900" };
  };


  const txnColumnStructure = [
    {
      title: "SN",
      key: "sn",
      width: 60,
      fixed: "left",
      render: (_, __, idx) => (
        <span className="text-sm text-gray-500">{(page - 1) * pageSize + idx + 1}</span>
      ),
    },
    {
      title: "Type",
      key: "type",
      width: 100,
      render: (_, tx) => {
        const entryType = (tx.entryType || tx.type).toUpperCase();
        return (
          <span className={`text-sm font-medium ${entryType == 'DEBIT' ? 'text-red-700' : entryType == 'CREDIT' ? 'text-green-700' : ''}`}>
            {entryType ? entryType : "—"}
          </span>
        );
      },
    },
    {
      title: "Date",
      key: "date",
      width: 140,
      render: (_, tx) => {
        const dateFormatted = fmtDate(tx.createdAt);
        if (!dateFormatted) return <span className="text-gray-400">—</span>;
        return <span className="text-sm text-gray-500 whitespace-nowrap">{dateFormatted}</span>;
      },
    },
    {
      title: "Status",
      key: "status",
      width: 120,
      render: (_, tx) => {
        const entryType = tx.entryType || tx.type;
        const status = tx.status || entryType;
        const modifiedStatus = statusLabel(status);
        return <Badge status={modifiedStatus} />;
      },
    },
    {
      title: "Amount",
      key: "amount",
      align: "right",
      width: 180,
      render: (_, tx) => {
        const { text, color } = amountDisplay(tx);
        return (
          <span className={`text-sm font-medium ${color}`}>
            {text} <span className="text-xs font-normal text-gray-400">{tx.currency || "USDC"}</span>
          </span>
        );
      },
    },
  ];

  return (
    <AppShell>
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Treasury</h1>
          <p className="text-sm text-gray-500 mt-0.5">{walletSubtitle}</p>
        </div>
        { (quotePermissions.canOnramp || quotePermissions.canOfframp) ? (
          <button
            onClick={() => navigate("/sender/treasury/fund")}
            className="bg-brand text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-brandHover transition-colors"
          >
            Fund Wallet
          </button>
        ) : null}
      </div>

      {/* Wallet balance card(s) — one per vault vendor (fiat, crypto, ...) */}
      {balanceLoading ? (
        <AnalyticsSummaryCards items={WALLET_BALANCE_SKELETON_ITEMS} loading />
      ) : (
        walletBalanceItems.length > 0 && <AnalyticsSummaryCards items={walletBalanceItems} />
      )}

      {/* Per-vendor vault ledgers, embedded in the balance response */}
      {!DISABLE_VAULT_LEDGER_TABLE && (
        balanceLoading ? (
          <VaultLedgerTable ledger={[]} title="Ledger" loading />
        ) : (
          vaultEntries.map((entry, idx) => (
            <VaultLedgerTable
              key={entry.balance?.vendor || idx}
              ledger={entry.ledger}
              vendor={entry.balance?.vendor}
              title="Ledger"
            />
          ))
        )
      )}

      {/* Vault ledger analytics */}
      {!DISABLE_VAULT_ANALYTICS && analyticsItems.length > 0 && (
        <AnalyticsSummaryCards
          heading="Vault Analytics"
          description="Overview of your vault ledger, per currency"
          items={analyticsItems}
          loading={vaultAnalyticsLoading}
          showDateFilter={!DISABLE_DATE_FILTER_FOR_ANALYTICS}
          onDateFilterChange={(_filter, range) =>
            setAnalyticsDateRange((prev) =>
              prev.startDate === range.startDate && prev.endDate === range.endDate ? prev : range
            )
          }
        />
      )}

      {/* Transactions table */}
      {!DISABLE_TRANSACTIONS_TABLE && (
      <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden shadow-xs">
        <div className="flex items-center justify-between gap-4 px-6 py-4 border-b border-gray-100">
          <div>
            <div className="flex items-center gap-3">
              <h2 className="text-sm font-semibold text-gray-800 m-0">Transactions</h2>
              {!loading && total > 0 && (
                <span className="text-xs font-medium bg-gray-100 text-gray-600 px-2.5 py-0.5 rounded-full border border-gray-200">
                  {total.toLocaleString()} total
                </span>
              )}
            </div>
            <p className="text-xs font-medium text-gray-500 m-0">Your vault ledger entries</p>
          </div>
          <div className="flex items-center gap-2">
            <Input.Search
              placeholder="Search by Status or..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              allowClear
              className="w-60"
            />
            {!DISABLE_DATE_FILTER_FOR_LEDGER && (
              <DateFilter onChange={changeLedgerDateFilter} />
            )}
          </div>
        </div>
        <Table
          className="admin-table table-blue-zebra"
          columns={txnColumnStructure}
          dataSource={txns}
          rowKey={(r) => r.id || `${r.transactionId || "row"}-${r.entryType || r.type || ""}-${r.currency || ""}-${r.createdAt}-${r.amount}`}
          loading={loading}
          scroll={{ x: "max-content" }}
          locale={{
            emptyText: loading ? (
              <div className="py-12" />
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No transactions in this window."
              />
            ),
          }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: { showSearch: false },
            showQuickJumper: false,
            pageSizeOptions: DEFAULT_PAGE_SIZE_OPTIONS.map(String),
            responsive: true,
            onShowSizeChange: (_current, newSize) => {
              setPageSize(newSize);
              setPage(1);
            },
            onChange: (newPage, newPageSize) => {
              setPage(newPage);
              if (newPageSize && newPageSize !== pageSize) {
                setPageSize(newPageSize);
                setPage(1);
              }
            },
            showTotal: (totalCount, range) => (
              <span className="text-xs text-gray-500 whitespace-nowrap">
                Showing{" "}
                <span className="font-medium text-gray-800">
                  {range[0]}-{range[1]}
                </span>{" "}
                of{" "}
                <span className="font-medium text-gray-800">
                  {totalCount}
                </span>{" "}
                transactions
              </span>
            ),
          }}
        />
      </div>
      )}
    </AppShell>
  );
};

export default Treasury;
